import React, { createContext, useContext, useState, useMemo } from "react";

const DICT = {
  fr: {
    // Topbar
    search_placeholder: "Rechercher marchés, partenaires, opportunités…",
    ai_trigger: "Ask Lodge",
    workspace: "Espace de travail",
    role_admin: "Administrateur",
    profile_menu: "Mon compte",
    // Sidebar
    nav_group_main: "Pilotage",
    nav_group_growth: "Croissance",
    nav_group_system: "Système",
    soon: "Bientôt",
    sidebar_footer: "Édition Enterprise",
    // Coming soon
    cs_badge: "En développement",
    cs_desc: "Ce module fait partie de la feuille de route Lodge & Co. Il sera activé dans une prochaine étape de la plateforme.",
    cs_notify: "Être notifié au lancement",
    cs_features: "Ce qui arrive",
    // Dashboard
    dash_title: "Tableau de bord",
    dash_subtitle: "Vue d'ensemble de la performance touristique de votre destination et de vos partenaires.",
    dash_period: "30 derniers jours",
    export: "Exporter",
    kpi_visitor_demand: "Visitor Demand",
    kpi_market_potential: "Market Potential",
    kpi_active_partners: "Active Partners",
    kpi_opportunities_short: "Opportunities",
    sec_perf_title: "Market Performance",
    sec_perf_sub: "Indice de demande pondéré · 12 mois",
    sec_season_title: "Seasonality",
    sec_season_sub: "Intensité de la demande par mois",
    sec_topmarkets_title: "Top Source Markets",
    sec_topmarkets_sub: "Part de la demande entrante",
    sec_aiopp_title: "AI Opportunities",
    sec_aiopp_sub: "Détectées par Lodge AI",
    kpi_vs_prev: "vs. période préc.",
    kpi_visibility: "Visibilité recherche",
    kpi_demand: "Demande estimée",
    kpi_opportunities: "Opportunités actives",
    kpi_conversion: "Taux d'activation",
    dash_demand_title: "Demande par mois",
    dash_demand_sub: "Volume de recherche pondéré · indice 100",
    dash_markets_title: "Top marchés émetteurs",
    dash_markets_sub: "Part de la demande entrante",
    dash_feed_title: "Signaux Lodge AI",
    dash_feed_sub: "Détections récentes du moteur d'intelligence",
    dash_channels_title: "Répartition des canaux",
    view_all: "Tout voir",
    // Markets
    markets_title: "Marchés",
    markets_subtitle: "Analyse de la demande internationale par marché émetteur.",
    markets_search: "Filtrer un marché…",
    m_col_market: "Marché",
    m_col_demand: "Demande",
    m_col_trend: "Tendance 12 m.",
    m_col_growth: "Croissance",
    m_col_spend: "Panier moyen",
    m_col_maturity: "Maturité",
    m_all_regions: "Toutes les régions",
    m_selected: "Marché sélectionné",
    // Opportunities
    opp_title: "Opportunités",
    opp_subtitle: "Opportunités commerciales détectées par Lodge AI, classées par potentiel.",
    opp_filter_all: "Toutes",
    opp_score: "Score",
    opp_potential: "Potentiel estimé",
    opp_horizon: "Horizon",
    opp_activate: "Activer",
    opp_details: "Détails",
    opp_ai_tag: "Détecté par IA",
    opp_impact: "Impact",
    opp_confidence: "Confiance",
  },
  en: {
    search_placeholder: "Search markets, partners, opportunities…",
    ai_trigger: "Ask Lodge",
    workspace: "Workspace",
    role_admin: "Administrator",
    profile_menu: "My account",
    nav_group_main: "Steering",
    nav_group_growth: "Growth",
    nav_group_system: "System",
    soon: "Soon",
    sidebar_footer: "Enterprise Edition",
    cs_badge: "In development",
    cs_desc: "This module is part of the Lodge & Co roadmap. It will be activated in an upcoming platform release.",
    cs_notify: "Notify me at launch",
    cs_features: "What's coming",
    dash_title: "Dashboard",
    dash_subtitle: "Overview of your destination's tourism performance and partners.",
    dash_period: "Last 30 days",
    export: "Export",
    kpi_visitor_demand: "Visitor Demand",
    kpi_market_potential: "Market Potential",
    kpi_active_partners: "Active Partners",
    kpi_opportunities_short: "Opportunities",
    sec_perf_title: "Market Performance",
    sec_perf_sub: "Weighted demand index · 12 months",
    sec_season_title: "Seasonality",
    sec_season_sub: "Demand intensity by month",
    sec_topmarkets_title: "Top Source Markets",
    sec_topmarkets_sub: "Share of inbound demand",
    sec_aiopp_title: "AI Opportunities",
    sec_aiopp_sub: "Detected by Lodge AI",
    kpi_vs_prev: "vs. prev. period",
    kpi_visibility: "Search visibility",
    kpi_demand: "Estimated demand",
    kpi_opportunities: "Active opportunities",
    kpi_conversion: "Activation rate",
    dash_demand_title: "Demand by month",
    dash_demand_sub: "Weighted search volume · index 100",
    dash_markets_title: "Top source markets",
    dash_markets_sub: "Share of inbound demand",
    dash_feed_title: "Lodge AI signals",
    dash_feed_sub: "Recent detections from the intelligence engine",
    dash_channels_title: "Channel breakdown",
    view_all: "View all",
    markets_title: "Markets",
    markets_subtitle: "International demand analysis by source market.",
    markets_search: "Filter a market…",
    m_col_market: "Market",
    m_col_demand: "Demand",
    m_col_trend: "12-mo trend",
    m_col_growth: "Growth",
    m_col_spend: "Avg. spend",
    m_col_maturity: "Maturity",
    m_all_regions: "All regions",
    m_selected: "Selected market",
    opp_title: "Opportunities",
    opp_subtitle: "Commercial opportunities detected by Lodge AI, ranked by potential.",
    opp_filter_all: "All",
    opp_score: "Score",
    opp_potential: "Estimated potential",
    opp_horizon: "Horizon",
    opp_activate: "Activate",
    opp_details: "Details",
    opp_ai_tag: "AI detected",
    opp_impact: "Impact",
    opp_confidence: "Confidence",
  },
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState("fr");
  const value = useMemo(() => ({ lang, setLang, t: (key) => DICT[lang][key] ?? key }), [lang]);
  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
}
